import { useEffect, useRef } from 'react'
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import AdministrationTabs from '../components/AdministrationTabs.jsx'
import ConfirmationModal from '../../../components/ConfirmationModal.jsx'
import DebugAccordion from '../../../components/DebugAccordion.jsx'
import LoadingAnimation from '../../../components/LoadingAnimation.jsx'
import PageTitle from '../../../components/PageTitle.jsx'
import TimestampWithUTCTooltip from '../../../components/TimestampWithUTCTooltip.jsx'

import { NotificationTypes, addNotification, notifyException } from '../../../state/actions/notificationActions.js'
import { fetchRole } from '../../../state/actions/roleActions.js'
import { fetchUser, updateUser, validateUsername, validateEmail, fetchUserRoles, fetchUserGroups } from '../../../state/actions/userActions.js'

import { userSignal, userLoadedSignal, userGroupsSignal, userRolesSignal } from '../../../state/signals/userSignals.js'

import { SystemUserType, SortDirectionType } from '../../../utils/constants.js'

export default function SystemUserProfilePage() {

    const navigate = useNavigate()
    const { beId, userId, systemUserType } = useParams()
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData } = useOutletContext()

    useEffect(() => {
		if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewSystemUsers) {
			addNotification('You are not authorized to view system users for this account.', NotificationTypes.ERROR, true)
			navigate(`/`)
		}
	}, [userPermissionsSummary.value, navigate])
    
    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUsers
        }
    )

    useEffect(() => {
        fetchUser(userId, config, accessToken)
        fetchUserGroups(userId, config, accessToken)
        fetchUserRoles(userId, config, accessToken).then(
            (roleBusinessEntities) => {
                console.log(roleBusinessEntities)
                roleBusinessEntities.map((role) => {
                    fetchRole(role.roleId, config, accessToken)
                })

            }

        )
    }, [userId, config, accessToken])

    const pageTabSignal = useSignal("profile")

    const getActiveUserStatus = () => {
        return userSignal.value.isActive ? "Yes" : "No"
    }

    const getLockedUserStatus = () => {
        return userSignal.value.isLocked ? "Yes" : "No"
    }

    const getInitialUserStatus = () => {
        // flip status meaning for has logged in display field
        return !userSignal.value.isInitialUser ? "Yes" : "No"
    }

    const getEmailVerifiedUserStatus = () => {
        return userSignal.value.emailAddressVerified ? "Yes" : "No"
    }

    const handleSubmitUpdateUser = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        userLoadedSignal.value = false
        try {
            let clonedData = structuredClone(userSignal.value)
            const [usernameResult, emailResult] = await Promise.all([
                validateUsername(userId, clonedData.username, config, accessToken),
                validateEmail(userId, clonedData.email, config, accessToken),
            ])

            let isAvailable = true
            if (!usernameResult?.isAvailable) {
                addNotification(`Username ${clonedData.username} is already in use`, NotificationTypes.ERROR, false, "Error")
                isAvailable = false
            }
            if (!emailResult?.isAvailable) {
                addNotification(`Email ${clonedData.email} is already in use`, NotificationTypes.ERROR, false, "Error")
                isAvailable = false
            }
            if (!isAvailable) return

            clonedData.lastUpdateDateTime = new Date().toISOString().replace('Z', '')
            updateUser(userId, clonedData, config, accessToken).then(
                () => {
                    addNotification("User was updated successfully", NotificationTypes.SUCCESS, false)
                    fetchUser(userId, config, accessToken)
                }
            )

        } catch (err) {
            addNotification("Unable to validate or update user", NotificationTypes.ERROR, false, "Error")
            console.error(err)
        } finally {
            userLoadedSignal.value = true
        }
    }

    const handleEditUserName = (e) => {
        const userNameRegex = new RegExp('^[A-Za-z0-9._@\\-]{1,50}$')
        if (userNameRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be between 1 and 50 valid characters: A-Z, a-z, 0-9, period (.), underscore (_), hyphen (-), or at-sign(@)")
        }
        console.log(e)
        userSignal.value = {
            ...userSignal.value,
            username: e.target.value
        }
    }

    const handleEditFirstName = (e) => {
        const firstNameRegex = new RegExp(`^[0-9A-Za-z .'\\-]{1,50}$`)
        if (firstNameRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be between 1 and 50 valid characters: 0-9, A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (')")
        }
        userSignal.value = {
            ...userSignal.value,
            firstName: e.target.value
        }
    }

    const handleEditLastName = (e) => {
        const lastNameRegex = new RegExp(`^[A-Za-z .'\\-]{1,50}$`)
        if (lastNameRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be between 1 and 50 valid characters: A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (')")
        }
        userSignal.value = {
            ...userSignal.value,
            lastName: e.target.value
        }
    }

    const handleEditEmail = (e) => {
        const emailRegex = new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i)
        if (emailRegex.test(e.target.value)) {
            e.target.setCustomValidity("")
        }
        else {
            e.target.setCustomValidity("Must be a valid email address i.e. sample@email.com")
        }
        userSignal.value = {
            ...userSignal.value,
            email: e.target.value
        }
    }

    const handleChangeUserStatus = () => {
        let isUserLocked = userSignal.value.isLocked
        let isUserActive = userSignal.value.isActive
        if (isUserLocked) {
            userSignal.value.isLocked = false
        }
        if (isUserActive) {
            userSignal.value.isActive = false
        } else {
            userSignal.value.isActive = true
        }
        userSignal.value.lastUpdateDateTime = new Date().toISOString().replace('Z', '')
        userLoadedSignal.value = false
        updateUser(userId, userSignal.value, config, accessToken).then(
            () => {
                addNotification('User status was updated successfully', NotificationTypes.SUCCESS, false)
                userLoadedSignal.value = true
            }
        ).catch(
            () => {
                addNotification(`Unable to change user status`, NotificationTypes.ERROR, false, 'User Status Change Fail')
            }
        )
    }

    if (!userLoadedSignal.value) {
        return (<LoadingAnimation />)
    }

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/systemUsers/serviceUsers` }}>System Users</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/systemUsers/serviceUsers/${userId}/`}}>{userSignal.value ? userSignal.value.username : "Loading..."}</Breadcrumb.Item>
                <Breadcrumb.Item active>Profile</Breadcrumb.Item>
            </Breadcrumb>
            <PageTitle title='User Profile' canEditSignal={canEditComputed} />
            <AdministrationTabs activeKey="5" />
            <Nav
                variant="tabs"
                size="sm"
                className="px-3 my-3"
                activeKey={pageTabSignal.value}
            >
                <Nav.Item>
                    <Link to={`/be/${beId}/management/systemUsers/${systemUserType}/${userId}`} eventKey="profile" className="nav-link active">Profile</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/systemUsers/${systemUserType}/${userId}/permissions`} eventKey="permissions" className="nav-link">Permissions</Link>
                </Nav.Item>
            </Nav>
            {(pageTabSignal.value === "profile") && (
                <>
                    <div id='form-container'>
                        <Row className="">
                            <Col sm={6} className="border-end">
                                <Form validated onSubmit={handleSubmitUpdateUser} >
                                    <Row className="m-2 d-flex justify-content-center">
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='userName' column sm={4} className='control-label'>Username</Form.Label>
                                                <Form.Control type='text' column sm={8} disabled={!canEditComputed.value} onChange={handleEditUserName} className="form-control username" id='userName' name='userName' maxLength='50' required={true} value={userSignal.value.username} />
                                                <Form.Control.Feedback type="invalid">Required. Username must be between 1 and 50 valid characters: A-Z, a-z, 0-9, period (.), underscore (_), hyphen (-), or at-sign(@)</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='fname' column sm={4} className='control-label'>First Name</Form.Label>
                                                <Form.Control type='text' column sm={8} disabled={!canEditComputed.value} onChange={handleEditFirstName} className="form-control person" id='fname' name='fname' maxLength='50' required={true} value={userSignal.value.firstName} />
                                                <Form.Control.Feedback type="invalid">Required. First Name must be between 1 and 50 valid characters: 0-9, A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (&apos;)</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='lname' column sm={4} className='control-label'>Last Name</Form.Label>
                                                <Form.Control type='text' column sm={8} disabled={!canEditComputed.value} onChange={handleEditLastName} className="form-control person" id='lname' name='lname' maxLength='50' required={true} value={userSignal.value.lastName} />
                                                <Form.Control.Feedback type="invalid">Required. Last Name must be between 1 and 50 valid characters: A-Z, a-z, period (.), hyphen (-), space ( ), or apostrophe (&apos;)</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                        <Form.Group as={Row}>
                                            <div className='col-md-8'>
                                                <Form.Label htmlFor='newEmail' column sm={4} className='control-label'>Email Address</Form.Label>
                                                <Form.Control type='email' column sm={8} disabled={!canEditComputed.value} onChange={handleEditEmail} placeholder='sample@email.com' className="form-control email" id='email' name='email' maxLength='200' required={true} value={userSignal.value.email} />
                                                <Form.Control.Feedback type="invalid">Required. Must be a valid email address i.e. sample@email.com</Form.Control.Feedback>
                                            </div>
                                        </Form.Group>
                                    </Row>
                                    <Row className="m-3 g-2">
                                        {canEditComputed.value && (
                                            <Col xs="auto">
                                                <Button type="submit">Apply</Button>
                                            </Col>
                                        )}
                                        <Col xs="auto">
                                            <Button variant="secondary" onClick={() => navigate(0)}>Cancel</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Col>
                            <Col sm={4}>
                                <Row className="m-3">
                                    <div className="m-2">
                                        Last Updated: <TimestampWithUTCTooltip>{userSignal.value.lastUpdateDateTime}</TimestampWithUTCTooltip>
                                    </div>
                                    <div className="m-2">Active: {getActiveUserStatus()}</div>
                                    <div className="m-2">Account Locked: {getLockedUserStatus()}</div>
                                    <div className="m-2">Email Verified: {getEmailVerifiedUserStatus()}</div>
                                    <div className="m-2">Has Logged In: {getInitialUserStatus()}</div>
                                    {(canEditComputed.value && userSignal.value.isActive) && (
                                        <Col xs="auto">
                                            <Button className="m-2 btn-warning" onClick={handleChangeUserStatus}>
                                                Disable
                                            </Button>
                                        </Col>
                                    )}
                                    {(canEditComputed.value && !userSignal.value.isActive) && (
                                        <Col xs="auto">
                                            <Button className="m-2 btn-warning" onClick={handleChangeUserStatus}>
                                                Enable
                                            </Button>
                                        </Col>
                                    )}
                                </Row>
                            </Col>
                        </Row >
                    </div>
                </>
            )}
            <DebugAccordion data={accordionData} /*debug={debug}*/ />
        </>
    )
}